import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib_app/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Copy, UserCheck, UserX, EyeOff, Eye } from "lucide-react";
import Avatar from "@/components/app/Avatar";
import TeamMemberProfileModal from "@/components/app/TeamMemberProfileModal";

const ROLE_LABEL = { owner: "Owner", sales_director: "Sales Director", sales: "Sales", finance: "Finance" };
const ROLE_TINT = {
  owner: "bg-[#DCE0E7] text-[#626061]",
  sales_director: "bg-[#DCE0E7] text-[#4E6D9A]",
  sales: "bg-[#DCE0E7] text-[#626061]",
  finance: "bg-[#FFE89A] text-[#7A6633]",
};

function randPw() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function Team() {
  const [users, setUsers] = useState([]);
  const [hideInactive, setHideInactive] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const { user: me } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () =>
    api.get(`/users?include_inactive=true`).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  // Support ?open=<user_id> from Global Search / deep links (waits until users loaded)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && users.length) {
      const u = users.find((x) => x.user_id === openId);
      if (u) setOpenUser(u);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  // Refresh the openUser after any save so the modal shows the latest values
  useEffect(() => {
    if (!openUser) return;
    const fresh = users.find((u) => u.user_id === openUser.user_id);
    if (fresh && fresh !== openUser) setOpenUser(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const sorted = useMemo(() => {
    const list = hideInactive ? users.filter((u) => u.is_active !== false) : users.slice();
    return list.sort((a, b) => {
      const aa = a.is_active === false ? 1 : 0;
      const bb = b.is_active === false ? 1 : 0;
      if (aa !== bb) return aa - bb; // active first
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [users, hideInactive]);

  const inactiveCount = users.filter((u) => u.is_active === false).length;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Manajemen</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Tim Sales</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Klik baris untuk melihat & mengubah data user. Semua aksi tersedia di dalam modal.</p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          data-testid="add-user-btn"
          className="gap-2 h-11 rounded-full bg-[#090808] hover:bg-[#000000] px-5 shadow-[0_6px_16px_rgba(9,8,8,0.25)]"
        >
          <UserPlus className="w-4 h-4" /> Tambah User
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <button
          onClick={() => setHideInactive((v) => !v)}
          data-testid="toggle-hide-inactive"
          className={`inline-flex items-center gap-2 h-9 px-4 rounded-full font-semibold text-xs transition-all ${
            hideInactive
              ? "bg-[#090808] text-white shadow-[0_4px_10px_rgba(24,27,51,0.20)]"
              : "bg-white text-[#626061] card-soft hover:text-[#090808]"
          }`}
        >
          {hideInactive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {hideInactive ? "Tampilkan yang nonaktif" : "Sembunyikan yang nonaktif"}
        </button>
        <span className="text-[11px] text-[#A0A3A8] font-medium">
          {sorted.length} user ditampilkan{inactiveCount > 0 && !hideInactive && ` · ${inactiveCount} nonaktif di bawah`}
        </span>
      </div>

      <Card className="border-0 rounded-2xl card-soft overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F5F7] text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">
            <tr>
              <th className="px-6 py-3.5 text-left">Nama</th>
              <th className="px-6 py-3.5 text-left">Email</th>
              <th className="px-6 py-3.5 text-left w-40">Role</th>
              <th className="px-6 py-3.5 text-left w-28">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => {
              const inactive = u.is_active === false;
              return (
                <tr
                  key={u.user_id}
                  data-testid={`user-row-${u.user_id}`}
                  onClick={() => setOpenUser(u)}
                  className={`border-t border-[#DCE0E7] cursor-pointer transition-colors ${
                    inactive ? "bg-slate-50/40 hover:bg-[#DCE0E7]/60 opacity-70" : "hover:bg-[#F4F5F7]"
                  }`}
                >
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={u.name} picture={u.picture} size="sm" />
                      <div>
                        <div className="text-[#090808] font-semibold">
                          {u.name} {u.user_id === me?.user_id && <span className="text-[10px] text-[#A0A3A8] font-normal">(kamu)</span>}
                        </div>
                        {u.nickname && u.nickname !== u.name && (
                          <div className="text-[11px] text-[#A0A3A8] font-medium">{u.nickname}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 text-[#626061]">{u.email}</td>
                  <td className="px-6 py-3.5">
                    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${ROLE_TINT[u.role] || "bg-[#DCE0E7] text-[#626061]"}`}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-6 py-3.5">
                    {inactive ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#B9C5D8] text-[#626061] font-semibold">
                        <UserX className="w-3 h-3" /> Nonaktif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#C9F0D6] text-[#4A6D53] font-semibold">
                        <UserCheck className="w-3 h-3" /> Aktif
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-sm text-[#A0A3A8]">Tidak ada user.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <AddUserModal open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <TeamMemberProfileModal
        user={openUser}
        users={users}
        currentUserId={me?.user_id}
        onOpenChange={() => setOpenUser(null)}
        onSaved={load}
      />
    </div>
  );
}

function AddUserModal({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(randPw());
  const [role, setRole] = useState("sales");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (open) {
      setName(""); setEmail(""); setPassword(randPw()); setRole("sales"); setCreated(null);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", { name, email, password, role });
      setCreated({ name, email, password, role });
      onCreated?.();
      toast.success("User dibuat. Bagikan kredensial ke sales.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat user");
    } finally {
      setBusy(false);
    }
  };

  const copyCreds = async () => {
    const text = `Email: ${created.email}\nPassword: ${created.password}\nLogin: ${window.location.origin}/login`;
    await navigator.clipboard.writeText(text);
    toast.success("Kredensial disalin");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-xl" data-testid="add-user-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            {created ? "User Berhasil Dibuat" : "Tambah User Baru"}
          </DialogTitle>
          <DialogDescription>
            {created ? "Bagikan kredensial berikut kepada anggota tim." : "Buat akun sales atau owner baru dengan password awal."}
          </DialogDescription>
        </DialogHeader>

        {!created ? (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Nama Lengkap</Label>
              <Input data-testid="new-user-name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Email</Label>
              <Input data-testid="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Password Awal</Label>
              <div className="flex gap-2">
                <Input data-testid="new-user-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <Button type="button" variant="outline" onClick={() => setPassword(randPw())} className="shrink-0">Acak</Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="sales_director">Sales Director</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
              <Button type="submit" disabled={busy} data-testid="new-user-submit" className="bg-[#090808] hover:bg-[#000000]">
                {busy ? "Membuat..." : "Buat User"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-2 font-num text-sm">
              <div><span className="text-zinc-500">Email:</span> {created.email}</div>
              <div><span className="text-zinc-500">Password:</span> {created.password}</div>
              <div><span className="text-zinc-500">Role:</span> {created.role}</div>
            </div>
            <p className="text-xs text-zinc-500">Salin dan kirimkan kepada sales via WhatsApp/email. Password tidak akan ditampilkan lagi setelah ditutup.</p>
            <DialogFooter>
              <Button variant="outline" onClick={copyCreds} className="gap-2"><Copy className="w-4 h-4" /> Salin</Button>
              <Button onClick={() => onOpenChange(false)} className="bg-[#090808] hover:bg-[#000000]">Selesai</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
